'use client';
import { merriweather } from '@/app/fonts';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useRouter, useSearchParams } from 'next/navigation';
import { X } from 'lucide-react';
import { useState } from 'react';

import {
  signIn,
  signOut,
  signInWithRedirect,
  resendSignUpCode,
} from 'aws-amplify/auth';

import { isAccountPermanentlySuspended } from '@/lib/checkAccountSuspended';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Form, FormControl, FormField, FormItem, FormMessage } from './ui/form';
import { toast } from 'sonner';
import { ConfirmSignupEmailOTP } from '@/components/confirm-signup-email-otp';
import { m } from 'framer-motion';

interface FormValues {
  email: string;
  password: string;
}

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<'form'>) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [cognitoUsername, setCognitoUsername] = useState<string>('');

  const form = useForm<FormValues>({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    try {
      const user = await signIn({
        username: values.email,
        password: values.password,
      });

      if (user.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        await resendSignUpCode({
          username: values.email,
        });
        setCognitoUsername(values.email);
      } else {
        // Block permanently-suspended accounts BEFORE entering the app, so they
        // never flash the home page. Cognito authenticates them; we sign them
        // straight back out and show the message here.
        if (await isAccountPermanentlySuspended()) {
          await signOut({ global: true });
          // Drive the message off the URL — local state would be lost when the
          // guard unmounts/remounts this form during sign-out.
          router.replace('/login?suspended=permanent');
          return;
        }
        // Check for redirect parameter. The middleware only ever sees the
        // pathname (fragments aren't sent to the server), but the browser
        // carries the original fragment over onto this /login URL per the
        // redirect spec, so window.location.hash still has it here.
        const redirectPath = searchParams.get('redirect');
        if (redirectPath && redirectPath.startsWith('/')) {
          router.push(redirectPath + window.location.hash);
        } else {
          // Redirect to /home for profile status check
          router.push('/home');
        }
      }
    } catch (e: any) {
      if (e.message) {
        form.setError('email', { message: e.message });
      } else {
        toast.error('Something went wrong', {
          icon: <X color="red" />,
        });
      }
    }
  };

  const handleLoginWithGoogle = async () => {
    try {
      await signOut({ global: true });
    } catch {
      // Ignore signOut errors - user may not be signed in
    }
    signInWithRedirect({
      provider: 'Google',
    });
  };

  if (cognitoUsername) {
    return (
      <ConfirmSignupEmailOTP
        cognitoUsername={cognitoUsername}
        onSuccessHref="/home"
      />
    );
  }

  return (
    <Form {...form}>
      <form
        className={cn('flex flex-col gap-6 px-2', className)}
        {...props}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        {searchParams.get('suspended') === 'permanent' && (
          <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-center text-red-700">
            Your account has been suspended and you can no longer access the
            site. If you believe this is a mistake, please
            <a
              href="mailto:support@weeamici.com"
              className="font-semibold underline hover:no-underline"
            >
              Contact Support.
            </a>{' '}
          </div>
        )}
        <div className={`${merriweather.className} grid gap-1`}>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="gap-0">
                <FormControl>
                  <Input
                    placeholder="Email"
                    aria-label="Email address"
                    {...field}
                    autoComplete="email"
                    data-testid="email-input"
                    className="h-12 border-1 border-[#cdc4c1] rounded-full md:text-xl text-lg text-center placeholder:text-black md:placeholder:text-xl placeholder:text-lg"
                  />
                </FormControl>
                <FormMessage data-testid="email-error" />
              </FormItem>
            )}
          />
          <a
            href="/reset-password"
            className="ml-auto inline-block text-md underline-offset-4 hover:underline"
          >
            Forgot password?
          </a>
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="gap-0">
                <FormControl>
                  <Input
                    type="password"
                    placeholder="Password"
                    aria-label="Password"
                    {...field}
                    autoComplete="current-password"
                    data-testid="password-input"
                    className="h-12 border-1 border-[#cdc4c1] md:text-xl text-lg rounded-full text-center placeholder:text-black placeholder:text-[20px] md:placeholder:text-xl placeholder:text-lg"
                  />
                </FormControl>
                <FormMessage data-testid="password-error" />
              </FormItem>
            )}
          />
          <div className="text-center my-4">
            <span className="bg-background text-lg text-muted-foreground relative z-10 px-2">
              or
            </span>
          </div>
          <Button
            type="button" // to skip form validation on google login
            variant="outline"
            className="md:display-none flex items-center justify-center w-full h-12 border-1 border-[#cdc4c1] rounded-full text-center md:text-xl text-lg relative"
            onClick={handleLoginWithGoogle}
          >
            <img
              src="/google-icon.png"
              alt=""
              aria-hidden="true"
              className="md:absolute md:left-6 select-none"
              width={50}
            />
            <p>Continue with Google</p>
          </Button>
          <p className="my-6 text-md text-justify">
            By continuing with sign up, you agree to our{' '}
            <span className="underline">
              <a href="/guidelines/forums">Community Guidelines</a>
            </span>
            ,{' '}
            <span className="underline">
              <a href="/privacy-policy">Privacy Policy</a>
            </span>
            ,{' '}
            <span className="underline">
              <a href="/cookies-policy">Cookie Policy</a>
            </span>
            , and{' '}
            <span className="underline">
              <a href="/member-agreement">Member Agreement.</a>
            </span>
          </p>
          <Button
            disabled={form.formState.isSubmitting}
            type="submit"
            className="w-full h-12 border-0 rounded-full text-center md:text-xl text-lg bg-[#cdc4c1]"
            data-testid="login-btn"
            loading={form.formState.isSubmitting}
          >
            Continue
          </Button>
        </div>
        <div className="text-center text-md ">
          Don&apos;t have an account?{' '}
          <a
            href="/signup"
            className="underline underline-offset-4 hover:underline"
          >
            Get started
          </a>
        </div>
        <div className="text-center text-md ">
          <span className="underline underline-offset-4 cursor-not-allowed text-gray-400 pointer-events-none" aria-disabled="true">
            Have an invite code?
          </span>
        </div>
      </form>
    </Form>
  );
}
